import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import ApiProvider from '../../../../../core/api/ApiProvider';
import { Loader } from '../../../../../core/components';
import { InfoMessageService, TranslationService } from '../../../../../core/services';
import UserLanguageService from '../../../../../core/services/UserLanguageService';
import { ResponseApiType } from '../../../../types/ResponseApiType';
import styles from './Timeline.module.scss';

type TimelineNote = {
  text: string;
  creator: string;
  recipient: string;
  direction: string;
  sentDate: string;
};

type TimelineEntry = {
  date: string;
  description: string;
  statusCode: string;
  category: string;
  status: string;
  messageTitle?: string;
  messageCreator?: string;
  notes?: TimelineNote[];
};

type TimelineData = {
  entries: TimelineEntry[];
};

const categoryConfig: Record<string, { iconClass: string; bgColor: string; labelKey: string }> = {
  messages: {
    iconClass: 'fas fa-envelope',
    bgColor: '#003f5f',
    labelKey: 'TimelineMessages',
  },
  payment: {
    iconClass: 'fas fa-check',
    bgColor: '#2e7d32',
    labelKey: 'TimelinePayment',
  },
  financial: {
    iconClass: 'fas fa-file-alt',
    bgColor: '#007698',
    labelKey: 'TimelineFinancial',
  },
  other: {
    iconClass: 'fas fa-circle',
    bgColor: '#6d6d6d',
    labelKey: 'TimelineOther',
  },
};

const translateCategory = (text: string): string => {
  return TranslationService.translate(text);
};

const CaseActivityTimeline = () => {
  const { caseNumber } = useParams() as { caseNumber: string };
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchTimeline = async () => {
    try {
      const result = await ApiProvider.post<ResponseApiType<TimelineData>>(
        'TimelineModule/GetTimeline',
        { CaseNo: caseNumber }
      );

      if (result.success) {
        setEntries(result.data.entries);
      }
    } catch (err) {
      console.error(err);
      InfoMessageService.error('ErrorSthWentWrong');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (entries.length === 0) {
    return (
      <div className={styles.emptyState}>{TranslationService.translate('TimelineNoData')}</div>
    );
  }

  const locale = UserLanguageService.getLanguageCode() || undefined;

  // Group entries by date to avoid repeating date info
  const groupedEntries: { dateLabel: string; items: TimelineEntry[] }[] = [];
  let lastDateKey = '';

  entries.forEach((entry) => {
    const date = new Date(entry.date);
    const dateKey = date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    if (dateKey === lastDateKey) {
      groupedEntries.at(-1)!.items.push(entry);
    } else {
      groupedEntries.push({ dateLabel: dateKey, items: [entry] });
      lastDateKey = dateKey;
    }
  });

  const renderEntry = (entry: TimelineEntry, index: number | string, isLast: boolean) => {
    const config = categoryConfig[entry.category] || categoryConfig.other;
    const date = new Date(entry.date);
    const timeStr = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <TimelineItem key={index}>
        <TimelineOppositeContent className={styles.timeLabel}>{timeStr}</TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot
            sx={{
              backgroundColor: config.bgColor,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
            }}
          >
            <i className={config.iconClass} style={{ fontSize: 14, color: '#fff' }} />
          </TimelineDot>
          {!isLast && <TimelineConnector sx={{ backgroundColor: '#e0e0e0', width: 3 }} />}
        </TimelineSeparator>
        <TimelineContent>
          <div className={styles.card} style={{ borderLeftColor: config.bgColor }}>
            <h3 className={styles.cardTitle}>
              {translateCategory(entry.messageTitle || entry.description)}
            </h3>
            {entry.notes && entry.notes.length > 0 && (
              <div className={styles.notesContainer}>
                {entry.notes.map((note) => (
                  <div
                    key={`${note.sentDate}-${note.creator}-${note.direction}`}
                    className={styles.note}
                  >
                    <p className={styles.noteText}>{note.text}</p>
                  </div>
                ))}
              </div>
            )}
            {!entry.notes?.length && entry.messageCreator && (
              <p className={styles.cardMeta}>{entry.messageCreator}</p>
            )}
          </div>
        </TimelineContent>
      </TimelineItem>
    );
  };

  return (
    <div className={styles.timelineWrapper}>
      <Timeline position="right" sx={{ padding: 0 }}>
        {groupedEntries.map((group, groupIndex) => {
          const isLastGroup = groupIndex === groupedEntries.length - 1;
          return (
            <React.Fragment key={group.dateLabel}>
              <li className={styles.dateHeader}>{group.dateLabel}</li>
              {group.items.map((entry, index) => {
                const isLastEntry = isLastGroup && index === group.items.length - 1;
                return renderEntry(entry, `${groupIndex}-${index}`, isLastEntry);
              })}
            </React.Fragment>
          );
        })}
      </Timeline>
    </div>
  );
};

export default CaseActivityTimeline;
